// Fictional people, places and figures used to illustrate where a claimed
// profile appears (claim and takedown pages). Illustration only: nothing here
// describes a real agent or agency. Photos are free-licence stock images
// served from /public/images/claim.

export type ShowcaseAgent = {
  name: string;
  photo: string;
  rating: string;
  sales: number;
  // The line under the name in the example ad, in the country's language.
  adStats: string;
};

export type CountryShowcase = {
  town: string;
  domain: string;
  address: string;
  homeValue: string;
  valueRange: string;
  pricePerSqm: string;
  agents: ShowcaseAgent[];
  agencies: { name: string; rating: string; sales: number }[];
  sold: { photo: string; rooms: number; date: string };
  // The example ad is shown as it runs: in the country's language.
  ad: {
    headlineStart: string;
    headlinePlace: string;
    headlineEnd: string;
    footer: string;
    link: string;
  };
};

const POLAND: CountryShowcase = {
  town: 'Kraków',
  domain: 'realadvisor.pl',
  address: 'ul. Długa 12, Kraków',
  homeValue: '712 000 zł',
  valueRange: '680 000 – 745 000 zł',
  pricePerSqm: '15 450 zł',
  agents: [
    {
      name: 'Katarzyna Wiśniewska',
      photo: '/images/claim/pl-agent-1.jpg',
      rating: '5.0',
      sales: 21,
      adStats: '5,0 · 7 opinii · 21 transakcji',
    },
    {
      name: 'Piotr Zieliński',
      photo: '/images/claim/pl-agent-2.jpg',
      rating: '4.9',
      sales: 14,
      adStats: '4,9 · 6 opinii · 14 transakcji',
    },
    {
      name: 'Magdalena Lewandowska',
      photo: '/images/claim/pl-agent-3.jpg',
      rating: '5.0',
      sales: 11,
      adStats: '5,0 · 5 opinii · 11 transakcji',
    },
    {
      name: 'Michał Kamiński',
      photo: '/images/claim/pl-agent-4.jpg',
      rating: '4.8',
      sales: 9,
      adStats: '4,8 · 5 opinii · 9 transakcji',
    },
    {
      name: 'Agnieszka Wójcik',
      photo: '/images/claim/pl-agent-5.jpg',
      rating: '5.0',
      sales: 8,
      adStats: '5,0 · 4 opinie · 8 transakcji',
    },
    {
      name: 'Tomasz Nowak',
      photo: '/images/claim/pl-agent-6.jpg',
      rating: '4.9',
      sales: 18,
      adStats: '4,9 · 4 opinie · 18 transakcji',
    },
  ],
  agencies: [
    { name: 'Wawel Nieruchomości', rating: '4.9', sales: 61 },
    { name: 'Kraków Home', rating: '4.8', sales: 39 },
    { name: 'Małopolska Domy', rating: '5.0', sales: 27 },
  ],
  sold: { photo: '/images/claim/pl-sold.jpg', rooms: 3, date: '2026-09-01' },
  ad: {
    headlineStart: '31 zweryfikowanych opinii ',
    headlinePlace: 'w Krakowie',
    headlineEnd: '. A jak jest u Ciebie?',
    footer: '6 lokalnych agentów · 81 transakcji w 24 mies. · średnia 5,0',
    link: 'Poznaj RealAdvisor Pro →',
  },
};

const PORTUGAL: CountryShowcase = {
  town: 'Porto',
  domain: 'realadvisor.pt',
  address: 'Rua de Cedofeita 120, Porto',
  homeValue: '385 000 €',
  valueRange: '362 000 – 409 000 €',
  pricePerSqm: '3 640 €',
  agents: [
    {
      name: 'Ana Ferreira',
      photo: '/images/claim/pt-agent-1.jpg',
      rating: '5.0',
      sales: 22,
      adStats: '5,0 · 7 avaliações · 22 vendas',
    },
    {
      name: 'Rui Costa',
      photo: '/images/claim/pt-agent-2.jpg',
      rating: '4.9',
      sales: 16,
      adStats: '4,9 · 6 avaliações · 16 vendas',
    },
    {
      name: 'Marta Oliveira',
      photo: '/images/claim/pt-agent-3.jpg',
      rating: '5.0',
      sales: 12,
      adStats: '5,0 · 5 avaliações · 12 vendas',
    },
    {
      name: 'Pedro Almeida',
      photo: '/images/claim/pt-agent-4.jpg',
      rating: '4.8',
      sales: 9,
      adStats: '4,8 · 4 avaliações · 9 vendas',
    },
    {
      name: 'Inês Rodrigues',
      photo: '/images/claim/pt-agent-5.jpg',
      rating: '5.0',
      sales: 7,
      adStats: '5,0 · 3 avaliações · 7 vendas',
    },
    {
      name: 'João Santos',
      photo: '/images/claim/pt-agent-6.jpg',
      rating: '4.9',
      sales: 14,
      adStats: '4,9 · 3 avaliações · 14 vendas',
    },
  ],
  agencies: [
    { name: 'Douro Imóveis', rating: '4.9', sales: 61 },
    { name: 'Casa Invicta', rating: '4.8', sales: 39 },
    { name: 'Ribeira Homes', rating: '5.0', sales: 27 },
  ],
  sold: { photo: '/images/claim/pt-sold.jpg', rooms: 2, date: '2026-09-01' },
  ad: {
    headlineStart: '28 avaliações verificadas ',
    headlinePlace: 'no Porto',
    headlineEnd: '. E as suas?',
    footer: '6 consultores locais · 80 vendas em 24 meses · média 5,0',
    link: 'Descobrir o RealAdvisor Pro →',
  },
};

// Only PT and PL profiles are scraped and claimable; any other tenant falls
// back to the Polish set rather than showing nothing.
export const getCountryShowcase = (
  countryCode: string | null | undefined,
): CountryShowcase => (countryCode === 'PT' ? PORTUGAL : POLAND);
